"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

interface Service {
  titleEn: string;
  titleZh: string;
  descEn: string;
  descZh: string;
  image: string;
  alt: string;
  span?: string;
}

const SERVICES: Service[] = [
{
  titleEn: "Full Home Renovation",
  titleZh: "全屋裝修",
  descEn: "Complete transformation from bare shell to move-in ready. We handle every detail.",
  descZh: "由清水房到交樓標準，全面翻新，每個細節我們一手包辦。",
  image: "https://images.unsplash.com/photo-1584798401744-1c5c3745a932",
  alt: "Modern Hong Kong living room full renovation with white walls and wooden flooring",
  span: "lg:col-span-2 lg:row-span-2"
},
{
  titleEn: "Interior Design",
  titleZh: "室內設計",
  descEn: "Bespoke design concepts that reflect your personality and lifestyle.",
  descZh: "度身訂造設計概念，彰顯您的個性與生活品味。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18c6d33c5-1769432645647.png",
  alt: "Luxury interior design with marble accents and designer furniture in Hong Kong apartment",
  span: "lg:col-span-1"
},
{
  titleEn: "Kitchen Renovation",
  titleZh: "廚房裝修",
  descEn: "Modern kitchens with premium cabinetry, countertops, and smart storage solutions.",
  descZh: "現代廚房設計，優質廚櫃、檯面及智能收納方案。",
  image: "https://images.unsplash.com/photo-1613545564267-b80e188a1541",
  alt: "Modern white kitchen renovation with marble countertops and integrated appliances",
  span: "lg:col-span-1"
},
{
  titleEn: "Bathroom Renovation",
  titleZh: "浴室翻新",
  descEn: "Spa-inspired bathrooms with premium tiles, fixtures, and waterproofing.",
  descZh: "水療級浴室設計，優質瓷磚、衛浴配件及防水工程。",
  image: "https://images.unsplash.com/photo-1700074817197-9e3c5e1b8c32",
  alt: "Luxury bathroom renovation with freestanding bathtub and marble tiles",
  span: "lg:col-span-1"
},
{
  titleEn: "Custom Furniture",
  titleZh: "訂造傢俬",
  descEn: "Built-in wardrobes, TV walls, and storage crafted to maximize every inch.",
  descZh: "內嵌衣櫃、電視牆及儲物空間，充分利用每寸空間。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_17fc9ac9e-1770738573663.png",
  alt: "Custom built-in wardrobe and storage furniture in modern bedroom",
  span: "lg:col-span-1"
},
{
  titleEn: "Office Renovation",
  titleZh: "辦公室裝修",
  descEn: "Commercial and office spaces designed for productivity, brand identity, and comfort.",
  descZh: "商業及辦公室空間設計，提升生產力、品牌形象及舒適度。",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d77a9752-1767196504972.png",
  alt: "Modern open-plan office renovation with collaborative workspace and natural lighting",
  span: "lg:col-span-2"
},
{
  titleEn: "Ceiling & Flooring",
  titleZh: "天花及地板",
  descEn: "Suspended ceilings, concealed lighting, hardwood, marble, and vinyl flooring.",
  descZh: "吊頂、暗藏燈光、實木、雲石及地膠地板工程。",
  image: "https://images.unsplash.com/photo-1675013272786-1b2635189614",
  alt: "Elegant ceiling with recessed lighting and herringbone hardwood flooring",
  span: "lg:col-span-1"
},
{
  titleEn: "Electrical & Plumbing",
  titleZh: "電力及水喉",
  descEn: "Full rewiring, smart home integration, and complete plumbing overhaul.",
  descZh: "全屋重新佈線、智能家居整合及完整水喉翻新工程。",
  image: "https://images.unsplash.com/photo-1606383446580-86e14df7ad48",
  alt: "Professional electrical wiring and plumbing installation during renovation",
  span: "lg:col-span-1"
}];


export default function ServicesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll(".service-reveal");
    if (!cards) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("hidden-reveal");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -60px 0px" }
    );

    cards.forEach((card) => {
      card.classList.add("hidden-reveal");
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" ref={sectionRef} className="py-24 md:py-32 px-6 md:px-12 max-w-screen-xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
        <div className="service-reveal reveal">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
              Our Services · 我們的服務
            </span>
          </div>
          <h2 className="font-display section-title text-white">
            一站式裝修<br />
            <span className="italic" style={{ color: "#D4AF37" }}>Full-Service</span> Renovation
          </h2>
        </div>
        <div className="service-reveal reveal max-w-sm">
          <p className="text-base leading-relaxed" style={{ color: "rgba(245,240,232,0.65)" }}>
            從設計規劃到施工完成，我們提供全面的室內裝修服務，
            確保每個項目達到最高標準。
          </p>
          <p className="text-sm mt-2" style={{ color: "rgba(245,240,232,0.45)" }}>
            From design planning to construction completion, we deliver comprehensive renovation services to the highest standards.
          </p>
        </div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[280px]">
        {SERVICES.map((service, index) =>
        <div
          key={`${service.titleEn}-${index}`}
          className={`service-reveal reveal service-card relative overflow-hidden rounded-2xl cursor-pointer group ${service.span || ""}`}
          style={{ transitionDelay: `${index * 80}ms` }}>
          
            {/* Image */}
            <div className="absolute inset-0">
              <AppImage
              src={service.image}
              alt={service.alt}
              fill
              className="object-cover transition-transform duration-700 group-hover:scale-105" />
            
            </div>

            {/* Overlay */}
            <div className="absolute inset-0 img-overlay" />
            <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{ background: "linear-gradient(135deg, rgba(10,22,40,0.85) 0%, rgba(26,47,94,0.7) 100%)" }} />
          

            {/* Gold accent line */}
            <div
            className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500"
            style={{ background: "linear-gradient(90deg, #D4AF37, #E8CC6A)" }} />
          

            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div
              className="text-xs font-bold uppercase tracking-widest mb-2 opacity-0 group-hover:opacity-100 transition-all duration-300"
              style={{ color: "#D4AF37" }}>
              
                {service.titleZh}
              </div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-white mb-2">
                {service.titleEn}
              </h3>
              <p
              className="text-sm leading-relaxed max-h-0 group-hover:max-h-20 overflow-hidden transition-all duration-500"
              style={{ color: "rgba(245,240,232,0.75)" }}>
              
                {service.descEn}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="service-reveal reveal mt-12 text-center">
        <a
          href="https://wa.me/85256614547"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105"
          style={{
            background: "linear-gradient(135deg, #D4AF37, #A8891E)",
            color: "#0A1628",
            boxShadow: "0 8px 30px rgba(212,175,55,0.3)"
          }}>
          
          免費諮詢 · Free Consultation
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </section>);

}